/**
 * "Request a number change" action for the locked account WhatsApp/login number.
 *
 * That number is verified once at signup and is where every login OTP goes, so
 * it is never self-service editable — the backend rejects any save that would
 * move it with HTTP 409 `ACCOUNT_NUMBER_LOCKED` (users/service.py
 * `_raise_account_number_locked`), and the phone field renders disabled.
 *
 * Until now the only thing the locked field offered was a dead-end "contact
 * admin at contact@gullivr.ai" hint. This turns that into an actual request:
 * the user submits the number they want plus a reason, and it is filed via
 * POST /api/v1/profile/request-number-change — which sends no OTP and changes
 * nothing, it just parks the ask on `pending_phone_change_request` and notifies
 * admins, who apply it from the admin Users page.
 */

import { useState } from "react";
import PhoneInputField from "../../components/ui/PhoneInputField";
import { apiClient } from "../../lib/http/apiClient";
import { parseApiError } from "../../lib/http/apiErrors";
import { useAuth } from "../../context/AuthContext";
import { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";

/** Shape of `pending_phone_change_request` on the profile / auth user. */
export interface PendingPhoneChangeRequest {
  requested_phone?: string | null;
  requested_phone_country_code?: string | null;
  requested_whatsapp?: string | null;
  requested_whatsapp_country_code?: string | null;
  reason?: string | null;
  requested_at?: string | null;
}

/** Split an E.164 string into the (country_code, national) pair the endpoint
 * wants. Mirrors `splitPhoneE164` in features/onboardingFlows/FieldRenderer. */
function splitE164(e164: string): { countryCode: string; national: string } {
  try {
    const parsed = parsePhoneNumber(e164);
    if (parsed?.countryCallingCode && parsed?.nationalNumber) {
      return { countryCode: `+${parsed.countryCallingCode}`, national: String(parsed.nationalNumber) };
    }
  } catch {
    /* fall through — validation below already blocks submitting a bad number */
  }
  return { countryCode: "", national: "" };
}

const RequestNumberChange: React.FC<{ pendingRequest?: PendingPhoneChangeRequest | null }> = ({
  pendingRequest,
}) => {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [newNumber, setNewNumber] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Local echo of a just-filed request, so the confirmation shows immediately
  // even before refreshUser() lands the server's copy.
  const [submitted, setSubmitted] = useState<PendingPhoneChangeRequest | null>(null);

  // `pendingRequest` is an explicit override for callers that already hold the
  // profile; otherwise read it off the auth user, which IS the /profile shape.
  const pending = submitted || pendingRequest || user?.pending_phone_change_request;

  if (pending) {
    return (
      <div className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
        <p className="text-xs font-medium text-amber-300">Number change requested</p>
        <p className="mt-1 text-xs text-foreground-muted">
          You asked us to change this to <span className="font-medium">{pending.requested_phone}</span>.
          An admin will review it and update your account.
        </p>
        {pending.reason ? (
          <p className="mt-1 text-xs text-foreground-muted">Reason: {pending.reason}</p>
        ) : null}
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1 text-xs font-medium text-brand underline underline-offset-2"
      >
        Request number change
      </button>
    );
  }

  const numberIsValid = !!newNumber && isValidPhoneNumber(newNumber);

  const submit = async () => {
    setError("");
    if (!numberIsValid) {
      setError("Enter a valid phone number including country code.");
      return;
    }
    const { countryCode, national } = splitE164(newNumber);
    if (!countryCode || !national) {
      setError("Enter a valid phone number including country code.");
      return;
    }
    setSubmitting(true);
    try {
      // The account keeps phone* and whatsapp* in sync, so one number covers
      // both — the backend mirrors the phone value onto WhatsApp when the
      // WhatsApp fields are omitted.
      const { data } = await apiClient.post<{ pending_phone_change_request?: PendingPhoneChangeRequest }>(
        "/api/v1/profile/request-number-change",
        {
          new_phone_country_code: countryCode,
          new_phone_number: national,
          reason: reason.trim(),
        },
      );
      setSubmitted(
        data?.pending_phone_change_request || {
          requested_phone: newNumber,
          reason: reason.trim(),
        },
      );
      // Pull the server's copy so a reload keeps showing the pending state.
      refreshUser().catch(() => {});
    } catch (err) {
      setError(parseApiError(err, "Couldn't submit your request. Please try again.").message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-2 space-y-3 rounded-lg border border-border bg-surface-subtle p-3">
      <p className="text-xs text-foreground-muted">
        Tell us the number you want to use and why. An admin will verify and update it — your
        current number keeps working until then.
      </p>
      <PhoneInputField
        label="New WhatsApp / login number"
        value={newNumber}
        onChange={setNewNumber}
        placeholder="New number"
        required
      />
      <div>
        <label className="mb-1 block text-xs font-medium text-foreground-muted" htmlFor="number-change-reason">
          Reason
        </label>
        <textarea
          id="number-change-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          maxLength={1000}
          placeholder="e.g. I changed my SIM and no longer have the old number"
          className="input w-full"
        />
      </div>
      {error ? <p className="text-xs text-red-500">{error}</p> : null}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={submitting || !numberIsValid}
          className="btn btn-primary btn-sm disabled:opacity-50"
        >
          {submitting ? "Submitting…" : "Submit request"}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setError("");
          }}
          disabled={submitting}
          className="btn btn-ghost btn-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default RequestNumberChange;
